import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentBlock, ContentState, EditorState, Modifier, convertToRaw } from 'draft-js';
import { CharacterMetadata, RichUtils, SelectionState, genKey } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import { List, Repeat } from 'immutable';

import '../../../node_modules/react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { useLanguageToolProofread } from './useLanguageToolProofread';
import { patchDraftJsDomObserver } from './patchDraftJsDomObserver';

// Guard Draft.js MutationObserver against decorator/extension DOM noise
// (Sentry B15658: nullthrows(findAncestorOffsetKey) → crash).
patchDraftJsDomObserver();

export type Mention = {
    text: string;
    value: string;
    url?: string;
}

interface WysiwygProps {
    value: string;
    onChange: (value: string, editorState: EditorState) => void;
    /** Fires on every Draft update, including selection-only moves. */
    onEditorStateChange?: (editorState: EditorState) => void;
    className?: string;
    rows?: number;
    minimal?: boolean;
    children?: JSX.Element | JSX.Element[];
    setRef?: (ref: HTMLTextAreaElement) => void;
    mentions?: Mention[];
};

/** Imperative API so parents can insert attachments without an HTML round-trip that resets the caret. */
export type WysiwygHandle = {
    applyEditorState: (nextState: EditorState) => void;
};

const fullToolbar = {
    options: ['inline', 'blockType', 'fontSize', 'fontFamily', 'list', 'textAlign', 'colorPicker', 'link', 'emoji', 'image', 'remove', 'history'],
    inline: {
        inDropdown: false,
        className: undefined,
        component: undefined,
        dropdownClassName: undefined,
        options: ['bold', 'italic', 'underline', 'strikethrough', 'monospace', 'superscript', 'subscript'],
    },
    blockType: {
        inDropdown: true,
        options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'Blockquote', 'Code'],
        className: undefined,
        component: undefined,
        dropdownClassName: undefined,
    },
    fontSize: {
        options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96],
        className: undefined,
        component: undefined,
        dropdownClassName: undefined,
    },
    fontFamily: {
        options: ['Arial', 'Georgia', 'Impact', 'Tahoma', 'Times New Roman', 'Verdana'],
        className: undefined,
        component: undefined,
        dropdownClassName: undefined,
    },
    list: {
        inDropdown: false,
        className: undefined,
        component: undefined,
        dropdownClassName: undefined,
        options: ['unordered', 'ordered', 'indent', 'outdent'],
    },
    textAlign: {
        inDropdown: false,
        className: undefined,
        component: undefined,
        dropdownClassName: undefined,
        options: ['left', 'center', 'right', 'justify'],
    },
    colorPicker: {
        className: undefined,
        component: undefined,
        popupClassName: undefined,
        colors: ['rgb(97,189,109)', 'rgb(26,188,156)', 'rgb(84,172,210)', 'rgb(44,130,201)',
                'rgb(147,101,184)', 'rgb(71,85,119)', 'rgb(204,204,204)', 'rgb(65,168,95)', 'rgb(0,168,133)',
                'rgb(61,142,185)', 'rgb(41,105,176)', 'rgb(85,57,130)', 'rgb(40,50,78)', 'rgb(0,0,0)',
                'rgb(247,218,100)', 'rgb(251,160,38)', 'rgb(235,107,86)', 'rgb(226,80,65)', 'rgb(163,143,132)',
                'rgb(239,239,239)', 'rgb(255,255,255)', 'rgb(250,197,28)', 'rgb(243,121,52)', 'rgb(209,72,65)',
                'rgb(184,49,47)', 'rgb(124,112,107)', 'rgb(209,213,216)'],
    },
    link: {
        inDropdown: false,
        className: undefined,
        component: undefined,
        popupClassName: undefined,
        dropdownClassName: undefined,
        showOpenOptionOnHover: true,
        defaultTargetOption: '_blank',
        options: ['link', 'unlink'],
        linkCallback: undefined
    },
    emoji: {
        className: undefined,
        component: undefined,
        popupClassName: undefined,
        emojis: [
            '😀', '😁', '😂', '😃', '😉', '😋', '😎', '😍', '😗', '🤗', '🤔', '😣', '😫', '😴', '😌', '🤓',
            '😛', '😜', '😠', '😇', '😷', '😈', '👻', '😺', '😸', '😹', '😻', '😼', '😽', '🙀', '🙈',
            '🙉', '🙊', '👼', '👮', '🕵', '💂', '👳', '🎅', '👸', '👰', '👲', '🙍', '🙇', '🚶', '🏃', '💃',
            '⛷', '🏂', '🏌', '🏄', '🚣', '🏊', '⛹', '🏋', '🚴', '👫', '💪', '👈', '👉', '👉', '👆', '🖕',
            '👇', '🖖', '🤘', '🖐', '👌', '👍', '👎', '✊', '👊', '👏', '🙌', '🙏', '🐵', '🐶', '🐇', '🐥',
            '🐸', '🐌', '🐛', '🐜', '🐝', '🍉', '🍄', '🍔', '🍤', '🍨', '🍪', '🎂', '🍰', '🍾', '🍷', '🍸',
            '🍺', '🌍', '🚑', '⏰', '🌙', '🌝', '🌞', '⭐', '🌟', '🌠', '🌨', '🌩', '⛄', '🔥', '🎄', '🎈',
            '🎉', '🎊', '🎁', '🎗', '🏀', '🏈', '🎲', '🔇', '🔈', '📣', '🔔', '🎵', '🎷', '💰', '🖊', '📅',
            '✅', '❎', '💯',
        ],
    },
    embedded: {
        className: undefined,
        component: undefined,
        popupClassName: undefined,
        embedCallback: undefined,
        defaultSize: {
            height: 'auto',
                width: 'auto',
        },
    },
    image: {
        className: undefined,
        component: undefined,
        popupClassName: undefined,
        urlEnabled: true,
        uploadEnabled: true,
        alignmentEnabled: true,
        uploadCallback: undefined,
        previewImage: false,
        inputAccept: 'image/gif,image/jpeg,image/jpg,image/png,image/svg',
        alt: { present: false, mandatory: false },
        defaultSize: {
            height: 'auto',
                width: 'auto',
        },
    },
    remove: { className: undefined, component: undefined },
    history: {
        inDropdown: false,
        className: undefined,
        component: undefined,
        dropdownClassName: undefined,
        options: ['undo', 'redo'],
    },
};

const simpleToolbar = {
    options: ['inline', 'blockType', 'list', 'link'],
    inline: {
        inDropdown: false,
        className: undefined,
        component: undefined,
        dropdownClassName: undefined,
        options: ['bold', 'italic', 'underline', 'strikethrough', 'monospace'],
    },
    blockType: {
        inDropdown: true,
        options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'Blockquote', 'Code'],
        className: undefined,
        component: undefined,
        dropdownClassName: undefined,
    },
    list: {
        inDropdown: false,
        className: undefined,
        component: undefined,
        dropdownClassName: undefined,
        options: ['unordered', 'ordered', 'indent', 'outdent'],
    },
    link: {
        inDropdown: false,
        className: undefined,
        component: undefined,
        popupClassName: undefined,
        dropdownClassName: undefined,
        showOpenOptionOnHover: true,
        defaultTargetOption: '_blank',
        options: ['link', 'unlink'],
        linkCallback: undefined
    }
};

/** Insert a LINK entity inline at the current selection (replacing any selected range). */
export const insertLink = (editorState: EditorState, linkText: string, linkUrl: string): EditorState | null => {
    if (!editorState)
        return null;

    const text = linkText || linkUrl;
    if (!text)
        return null;

    const contentStateWithEntity = editorState.getCurrentContent().createEntity(
        'LINK',
        'MUTABLE',
        { url: linkUrl, target: '_blank' }
    );
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const selection = editorState.getSelection();

    const newContent = selection.isCollapsed()
        ? Modifier.insertText(contentStateWithEntity, selection, text, undefined, entityKey)
        : Modifier.replaceText(contentStateWithEntity, selection, text, undefined, entityKey);

    const newEditorState = EditorState.push(editorState, newContent, 'insert-characters');
    return EditorState.forceSelection(newEditorState, newContent.getSelectionAfter());
}

/** True when a Draft block is visually empty (blank line / `<br>`-only paste). */
export const isEmptyPasteBlock = (block: ContentBlock): boolean => {
    if (!block)
        return true;
    if (block.getType() !== 'unstyled')
        return false;
    return block.getText().replace(/\u00a0/g, ' ').trim() === '';
};

/**
 * Drop empty unstyled blocks produced by HTML paste (`<p></p>`, `<p><br></p>`,
 * `<div><br></div>`). Keeps intentional structure (lists, headings, etc.).
 */
export const stripEmptyPasteBlocks = (blocks: ContentBlock[]): ContentBlock[] => {
    if (!blocks || !blocks.length)
        return [];
    const stripped = blocks.filter((block) => !isEmptyPasteBlock(block));
    return stripped.length ? stripped : blocks.slice(0, 1);
};

/** Turn plain clipboard text into HTML paragraphs; single `\n` → soft `<br>`. */
export const plainTextToPasteHtml = (text: string): string => {
    const normalized = (text || '').replace(/\r\n?/g, '\n');
    if (!normalized.trim())
        return '';

    return normalized
        .split(/\n{2,}/)
        .map((paragraph) => paragraph.replace(/^\n+|\n+$/g, ''))
        .filter((paragraph) => paragraph.length > 0)
        .map((paragraph) => {
            const escaped = paragraph
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/\n/g, '<br>');
            return `<p>${escaped}</p>`;
        })
        .join('');
};

/** Hard cap: huge clipboard HTML (Word/Outlook/Slack) freezes html-to-draftjs. */
export const LARGE_HTML_PASTE_CHARS = 4_000;

/**
 * Prefer plain clipboard text over HTML when the HTML payload is expensive.
 * Keeps intentional rich paste for small snippets; avoids main-thread freezes.
 */
export const shouldPreferPlainTextPaste = (
    text: string,
    html: string | undefined,
): boolean => {
    if (!html || !text)
        return false;
    if (html.length > LARGE_HTML_PASTE_CHARS)
        return true;
    // Styled email / messenger wrappers: HTML much heavier than visible text.
    if (html.length > 500 && html.length > text.length * 4)
        return true;
    return false;
};

const insertBlocksAtCursor = (
    editorState: EditorState,
    contentBlocks: ContentBlock[],
    entityMap?: any,
): EditorState => {
    if (!contentBlocks || !contentBlocks.length)
        return editorState;

    const fragmentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
    const newContent = Modifier.replaceWithFragment(
        editorState.getCurrentContent(),
        editorState.getSelection(),
        fragmentState.getBlockMap()
    );
    const nextState = EditorState.push(editorState, newContent, 'insert-fragment');
    return EditorState.forceSelection(nextState, newContent.getSelectionAfter());
};

/**
 * Insert HTML at the current selection (replacing any selected range).
 * Falls back to the original state when the HTML cannot be parsed.
 */
export const insertHtmlAtCursor = (editorState: EditorState, html: string): EditorState => {
    if (!editorState)
        return editorState;

    const trimmed = (html || '').trim();
    if (!trimmed)
        return editorState;

    let contentBlocks;
    let entityMap;
    try {
        const parsed = htmlToDraft(trimmed);
        contentBlocks = stripEmptyPasteBlocks(parsed.contentBlocks || []);
        entityMap = parsed.entityMap;
    } catch (_e) {
        return editorState;
    }

    return insertBlocksAtCursor(editorState, contentBlocks, entityMap);
};

/**
 * Custom paste handler for react-draft-wysiwyg.
 * Strips empty blocks that html-to-draftjs keeps (extra blank lines on paste).
 * Returns true when handled so Draft's default path is skipped.
 */
export const handlePastedText = (
    text: string,
    html: string | undefined,
    editorState: EditorState,
    onChange: (editorState: EditorState) => void,
): boolean => {
    if (!editorState)
        return false;

    const currentBlock = editorState
        .getCurrentContent()
        .getBlockForKey(editorState.getSelection().getStartKey());
    if (currentBlock && currentBlock.getType() === 'code')
        return false;

    let contentBlocks: ContentBlock[] | undefined;
    let entityMap;

    try {
        const usePlain = shouldPreferPlainTextPaste(text, html) || !html;
        if (!usePlain && html) {
            const parsed = htmlToDraft(html);
            if (!parsed?.contentBlocks?.length)
                return false;
            contentBlocks = stripEmptyPasteBlocks(parsed.contentBlocks);
            entityMap = parsed.entityMap;
        } else if (text) {
            const pasteHtml = plainTextToPasteHtml(text);
            if (!pasteHtml)
                return false;
            const parsed = htmlToDraft(pasteHtml);
            if (!parsed?.contentBlocks?.length)
                return false;
            contentBlocks = stripEmptyPasteBlocks(parsed.contentBlocks);
            entityMap = parsed.entityMap;
        } else {
            return false;
        }
    } catch (_e) {
        return false;
    }

    if (!contentBlocks?.length)
        return false;

    onChange(insertBlocksAtCursor(editorState, contentBlocks, entityMap));
    return true;
};

export const insertImage = (editorState: EditorState, src: string, alt: string): EditorState | null => {
    if (!editorState)
        return null;

    const selectionState  = editorState.getSelection();
    const contentState    = editorState.getCurrentContent();
    const currentBlock    = contentState.getBlockForKey(selectionState.getStartKey());
    const currentBlockKey = currentBlock.getKey();
    const blockMap        = contentState.getBlockMap();
    const blocksBefore    = blockMap.toSeq().takeUntil((v) => (v === currentBlock));
    const blocksAfter     = blockMap.toSeq().skipUntil((v) => (v === currentBlock)).rest();
    const newBlockKey     = genKey();

    // add new ContentBlock to editor state with appropriate text
    const list: any = (List(Repeat(CharacterMetadata.create(), alt.length)) as any);
    const newBlock = new ContentBlock({
        key: newBlockKey,
        type: 'unstyled',
        text: alt,
        characterList: list
    });

    const newBlockMap = blocksBefore.concat(
        [[currentBlockKey, currentBlock], [newBlockKey, newBlock]],
        blocksAfter
    ).toOrderedMap();

    const selection = editorState.getSelection();

    const newContent = contentState.merge({
        blockMap: newBlockMap,
        selectionBefore: selection,
        selectionAfter: selection.merge({
            anchorKey: newBlockKey,
            anchorOffset: 0,
            focusKey: newBlockKey,
            focusOffset: 0,
            isBackward: false,
        }),
    });

    let newEditorState = EditorState.push(editorState, newContent as ContentState, 'split-block');

    // programmatically apply selection on this text
    let newSelection = new SelectionState({
        anchorKey: newBlockKey,
        anchorOffset: 0,
        focusKey: newBlockKey,
        focusOffset: alt.length
    });

    newEditorState = EditorState.forceSelection(newEditorState, newSelection);

    // create link entity
    const newContentState = newEditorState.getCurrentContent();

    const contentStateWithEntity = newContentState.createEntity(
        'IMAGE',
        'IMMUTABLE',
        { src, alt, style: { width: '100%', height: '100%' }}
    );

    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    newEditorState = EditorState.set(newEditorState, { currentContent: contentStateWithEntity });

    newEditorState = RichUtils.toggleLink(newEditorState, newEditorState.getSelection(), entityKey);

    // reset selection
    newSelection = new SelectionState({
        anchorKey: newBlockKey,
        anchorOffset: alt.length,
        focusKey: newBlockKey,
        focusOffset: alt.length
    });

    return EditorState.forceSelection(newEditorState, newSelection);
}

/**
 * True when `incomingValue` is the same editor content we just emitted, possibly
 * after html↔draft normalization. Used to avoid recreating EditorState (which
 * resets the cursor) on controlled prop echoes — especially after paste.
 */
export const isEchoedEditorContent = (
    incomingValue: string,
    lastEditorHtml: string,
    canonicalize: (html: string) => string,
): boolean => {
    const rawValue = incomingValue || '';
    const lastHtml = lastEditorHtml || '';
    const targetCanonical = canonicalize(rawValue);

    return (
        rawValue === lastHtml ||
        targetCanonical === lastHtml ||
        targetCanonical === canonicalize(lastHtml)
    );
};

/** Keep LINK/IMAGE entities across html↔draft round-trips (omit entityMap → plain text). */
export const toCanonicalHtml = (html: string) => {
    const contentBlock = htmlToDraft(html || "");
    const cState = ContentState.createFromBlockArray(
        contentBlock.contentBlocks,
        contentBlock.entityMap
    );
    return draftToHtml(convertToRaw(cState));
};

export const editorStateFromHtml = (html: string): EditorState => {
    try {
        const contentBlock = htmlToDraft(html || "");
        if (!contentBlock.contentBlocks?.length)
            return EditorState.createEmpty();
        return EditorState.createWithContent(
            ContentState.createFromBlockArray(contentBlock.contentBlocks, contentBlock.entityMap)
        );
    } catch (_e) {
        return EditorState.createEmpty();
    }
};

const toEditorState = (html: string) => editorStateFromHtml(html);

const Wysiwyg = forwardRef<WysiwygHandle, WysiwygProps>((props, ref) => {
    const { i18n } = useTranslation();

    const {
        children, value, onChange, onEditorStateChange: onEditorStateChangeProp,
        className, rows, minimal, mentions, setRef,
    } = props;

    const [editorState, setEditorState] = useState<EditorState>(() => toEditorState(value || ""));
    const lastAppliedFromPropsRef       = React.useRef<string>(toCanonicalHtml(value || ""));
    const lastContentFromEditorRef      = React.useRef<string>(toCanonicalHtml(value || ""));
    const lastContentStateRef           = React.useRef(editorState.getCurrentContent());
    const wrapperRef                    = React.useRef<HTMLDivElement>(null);
    const onChangeRef                   = React.useRef(onChange);
    onChangeRef.current = onChange;
    const onEditorStateChangePropRef    = React.useRef(onEditorStateChangeProp);
    onEditorStateChangePropRef.current = onEditorStateChangeProp;

    useEffect(() => {
        const rawValue = value || "";

        // Cheap echo path: skip html↔draft round-trips after paste/typing.
        if (
            rawValue === lastContentFromEditorRef.current ||
            rawValue === lastAppliedFromPropsRef.current
        ) {
            lastAppliedFromPropsRef.current = rawValue;
            return;
        }

        const targetCanonical = toCanonicalHtml(rawValue);
        if (targetCanonical === lastAppliedFromPropsRef.current)
            return;

        // Controlled echo of what the editor just produced: keep live EditorState
        // so selection survives paste / typing when HTML round-trips differ.
        if (isEchoedEditorContent(rawValue, lastContentFromEditorRef.current, toCanonicalHtml)) {
            lastAppliedFromPropsRef.current = targetCanonical;
            return;
        }

        lastAppliedFromPropsRef.current = targetCanonical;
        lastContentFromEditorRef.current = targetCanonical;
        setEditorState((prevState) => {
            const prevHtml = draftToHtml(convertToRaw(prevState.getCurrentContent()));
            if (isEchoedEditorContent(rawValue, prevHtml, toCanonicalHtml))
                return prevState;
            const next = toEditorState(rawValue);
            lastContentStateRef.current = next.getCurrentContent();
            return next;
        });
    }, [value]);

    const setEditorReference = (editorRef: HTMLTextAreaElement) => {
        setRef && setRef(editorRef);
    }

    const emitContentChange = useCallback((nextState: EditorState) => {
        const nextContent = nextState.getCurrentContent();
        lastContentStateRef.current = nextContent;

        const content = draftToHtml(convertToRaw(nextContent));
        lastContentFromEditorRef.current = content;
        lastAppliedFromPropsRef.current = toCanonicalHtml(content);

        if (content === '<p></p>\n') {
            onChangeRef.current('', nextState);
            return;
        }
        onChangeRef.current(content, nextState);
    }, []);

    useImperativeHandle(ref, () => ({
        applyEditorState: (nextState: EditorState) => {
            setEditorState(nextState);
            onEditorStateChangePropRef.current?.(nextState);
            emitContentChange(nextState);
        },
    }), [emitContentChange]);

    const onEditorStateChange = useCallback((nextState: EditorState) => {
        setEditorState(nextState);
        onEditorStateChangePropRef.current?.(nextState);

        // Draft keeps the same ContentState reference on selection-only updates —
        // skip expensive convertToRaw/draftToHtml in that case.
        const nextContent = nextState.getCurrentContent();
        if (nextContent === lastContentStateRef.current)
            return;
        lastContentStateRef.current = nextContent;

        const content = draftToHtml(convertToRaw(nextContent));

        // Ignore cursor/selection-only updates for content onChange.
        if (content === lastContentFromEditorRef.current)
            return;
        lastContentFromEditorRef.current = content;

        // Ignore one-way updates coming from props.
        if (
            content === lastAppliedFromPropsRef.current ||
            toCanonicalHtml(content) === lastAppliedFromPropsRef.current
        )
            return;

        // remove default empty paragraph
        if (content === '<p></p>\n')
            return onChangeRef.current('', nextState);

        onChangeRef.current(content, nextState);
    }, []);

    const { enabled: languageToolEnabled, customDecorators } = useLanguageToolProofread({
        editorState,
        onEditorStateChange,
        wrapperRef,
    });

    // Opt out of browser LanguageTool / spellcheck on the contenteditable —
    // they mutate Draft's DOM and cause freezes, ghost text, and lost selection.
    // In-app LT (decorators) remains controlled via settings.
    useEffect(() => {
        const root = wrapperRef.current;
        if (!root)
            return undefined;
        const apply = () => {
            const editable = root.querySelector<HTMLElement>('[contenteditable="true"]');
            if (!editable)
                return;
            editable.setAttribute('data-lt-active', 'false');
            editable.setAttribute('spellcheck', 'false');
        };
        apply();
        // rdw remounts contenteditable after the LT on/off key change.
        const raf = requestAnimationFrame(apply);
        return () => cancelAnimationFrame(raf);
    }, [languageToolEnabled]);

    const mentionConfig = mentions ? {
        separator: ' ',
        trigger: '@',
        suggestions: mentions
    } : undefined;
    return (
        <div
            ref={wrapperRef}
            className={`Wysiwyg ${className}`}
            data-lt-active="false"
        >
            <Editor
                // rdw builds CompositeDecorator only at mount — remount when LT toggles.
                key={languageToolEnabled ? 'wysiwyg-lt-on' : 'wysiwyg-lt-off'}
                editorRef={setEditorReference}
                editorState={editorState}
                wrapperClassName=""
                editorClassName=""
                editorStyle={{ minHeight: `${(rows || 4) * 2}rem`, overflow: 'auto'}}
                onEditorStateChange={onEditorStateChange}
                handlePastedText={handlePastedText}
                spellCheck={false}
                localization={{
                    locale: i18n.language
                }}
                toolbar={ minimal ? simpleToolbar : fullToolbar }
                mention={ mentionConfig }
                customDecorators={customDecorators}
            />
            <div>
                {children}
            </div>
        </div>
    );
});

Wysiwyg.displayName = 'Wysiwyg';

export default Wysiwyg;
